import React, { MouseEvent } from 'react';
import { OutliersData } from 'widgets/outliers/data/outliersData';
import './OutliersChart.scss';
import { OutliersChartSeries } from 'widgets/outliers/components/chart/series/outliersChartSeries';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import classNames from 'classnames';
import {
    BubbleMouseOver,
    getOrderIdsForBubblesUnderMouse,
    useBubbleMouseOverHandler,
} from 'widgets/outliers/components/chart/BubbleMouseOver';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';
import OutliersChartLegends from './OutliersChartLegends';
import { useOutliersConfigSlice } from 'widgets/outliers/store/config/configSlice';
import { Dimensions } from 'utils/types/Dimensions';
import { CartesianChart } from 'utils/components/CartesianChart/CartesianChart';
import { useWidgetContext } from 'widgets/WidgetContext';
import { useOutlierWidgetDimensions } from 'widgets/outliers/components/hooks';
import { useOutliersTickFormats } from 'widgets/outliers/components/chart/axisStrategies';
import { OutliersTitlePanel } from 'widgets/outliers/components/title/OutliersTitlePanel';

interface OutliersChartProps {
    bubbleMouseOver: BubbleMouseOver;
    chartSeries: OutliersChartSeries;
    data: OutliersData;
    selectedOrderIds: string[];
    onSetSelectedOrderIds: (ids: string[]) => void;
    onlyShowSelected: boolean;
    onPlotAreaDimensionsChanged: (nextDimensions: Dimensions) => void;
}
export function OutliersChart(props: OutliersChartProps) {
    const {
        bubbleMouseOver,
        chartSeries,
        data,
        selectedOrderIds,
        onlyShowSelected,
        onSetSelectedOrderIds,
        onPlotAreaDimensionsChanged,
    } = props;

    const { widgetKey } = useWidgetContext();
    const { mainPanelHeight: height, mainPanelWidth: width } =
        useOutlierWidgetDimensions();

    const { items: dataItems } = data;
    const { combineSellsAndShorts } = useSelectAppSettings();
    const { config } = useOutliersConfigSlice();

    const handleMouseOver = useBubbleMouseOverHandler(bubbleMouseOver);

    const onClickChart = (e: MouseEvent<HTMLDivElement>) => {
        const orderIds = getOrderIdsForBubblesUnderMouse(e);

        const items = dataItems.filter(
            (d) => orderIds.indexOf(d.orderID.toString()) > -1
        );
        if (orderIds.length > 0) {
            // When CTRL pressed use all orders under cursor, otherwise use order with the largest value...
            const clickedOrderIds = e.ctrlKey
                ? items.map((i) => i.orderID.toString())
                : [orderBy(items, ['value'], ['asc'])[0].orderID.toString()];

            if (e.shiftKey) {
                // Shifts adds to the selection...
                const nextOrderIds = uniq([
                    ...selectedOrderIds,
                    ...clickedOrderIds,
                ]);
                onSetSelectedOrderIds(nextOrderIds);
            } else {
                // Otherwise selection is replaced...
                onSetSelectedOrderIds(clickedOrderIds);
            }
        } else {
            if (!e.shiftKey) {
                onSetSelectedOrderIds([]);
            }
        }
    };

    const style = { height: `${height}px`, width: `${width}px` };
    const chartHeight = config.hideTitlePanel ? height : height - 22;

    const cssClasses = classNames(
        'OutliersChart',
        !combineSellsAndShorts && 'shorts-sep',
        onlyShowSelected && selectedOrderIds.length > 0 && 'only-show-selected'
    );

    const { xTickFormat, yTickFormat } = useOutliersTickFormats();

    return (
        <div className={cssClasses} style={style}>
            <OutliersTitlePanel />
            <CartesianChart
                autoSizeYAxis={true}
                chartSeries={chartSeries.series}
                data={dataItems}
                id={'OutliersChart_' + widgetKey}
                xTickFormat={xTickFormat}
                yTickFormat={yTickFormat}
                height={chartHeight}
                width={width}
                onMouseMove={handleMouseOver}
                onClickChart={onClickChart}
                onPlotAreaDimensionsChanged={onPlotAreaDimensionsChanged}
            />
            <OutliersChartLegends
                chartHeight={chartHeight}
                config={config}
                data={data}
                mouseOver={bubbleMouseOver}
            />
        </div>
    );
}
